// Self-contained "rough location" UK map. The outline and the plotted dot use
// the SAME equirectangular projection, so dots always sit correctly on the shape.
// No tiles, no network — works offline.

const LON0 = -8.6, LON1 = 2.0, LAT0 = 49.8, LAT1 = 59.0;
const K = Math.cos((54 * Math.PI) / 180); // longitude squeeze at ~54°N
const XEXT = (LON1 - LON0) * K;
const YEXT = LAT1 - LAT0;
export const VIEW_W = 100;
export const VIEW_H = +(100 * (YEXT / XEXT)).toFixed(1); // ~148

export function project(lat, lon) {
  const x = ((lon - LON0) * K) / XEXT * VIEW_W;
  const y = (LAT1 - lat) / YEXT * VIEW_H;
  return [x, y];
}

// Rough coastline (clockwise). Recognisable, not survey-accurate.
const GB = [
  [58.67, -3.4], [57.6, -1.8], [56.05, -2.6], [55.0, -1.4], [54.1, -0.1], [53.6, 0.1],
  [52.9, 1.75], [52.0, 1.6], [51.4, 1.4], [50.9, 0.3], [50.6, -1.9], [50.2, -3.6],
  [50.05, -5.7], [51.2, -4.2], [51.6, -5.1], [52.8, -4.7], [53.3, -3.2], [53.4, -3.0],
  [54.1, -3.2], [54.9, -3.5], [55.3, -5.7], [55.9, -5.6], [56.6, -6.2], [57.5, -5.9],
  [58.5, -5.0],
];
const NI = [
  [54.05, -6.9], [54.1, -5.5], [54.7, -5.7], [55.24, -6.51], [55.2, -7.3], [54.5, -8.1],
];

const toPath = (pts) => pts.map(([la, lo], i) => `${i ? "L" : "M"}${project(la, lo).map((n) => n.toFixed(1)).join(" ")}`).join("") + "Z";
export const GB_PATH = toPath(GB);
export const NI_PATH = toPath(NI);

// City / region keyword centroids (checked as substrings, case-insensitive).
const PLACES = [
  ["london", 51.5, -0.12], ["south east", 51.2, 0.5], ["south west", 50.8, -3.6],
  ["east of england", 52.4, 0.6], ["east anglia", 52.5, 1.0], ["norfolk", 52.7, 1.1],
  ["suffolk", 52.2, 1.0], ["essex", 51.7, 0.5], ["kent", 51.27, 0.9], ["sussex", 50.9, 0.1],
  ["surrey", 51.25, -0.4], ["hampshire", 51.0, -1.3], ["dorset", 50.7, -2.4], ["devon", 50.7, -3.7],
  ["cornwall", 50.4, -4.9], ["somerset", 51.1, -3.0], ["bristol", 51.45, -2.6], ["gloucester", 51.86, -2.24],
  ["oxford", 51.75, -1.26], ["berkshire", 51.45, -1.0], ["west midlands", 52.48, -1.9], ["birmingham", 52.48, -1.9],
  ["east midlands", 52.9, -1.2], ["nottingham", 52.95, -1.15], ["leicester", 52.63, -1.13], ["derby", 52.92, -1.48],
  ["lincoln", 53.23, -0.54], ["yorkshire", 53.9, -1.4], ["leeds", 53.8, -1.55], ["sheffield", 53.38, -1.47],
  ["hull", 53.74, -0.33], ["north east", 54.9, -1.6], ["newcastle", 54.98, -1.6], ["durham", 54.78, -1.57],
  ["sunderland", 54.9, -1.38], ["north west", 53.6, -2.6], ["manchester", 53.48, -2.24], ["liverpool", 53.41, -2.99],
  ["lancashire", 53.8, -2.6], ["cumbria", 54.5, -3.0], ["cheshire", 53.2, -2.5], ["merseyside", 53.42, -2.96],
  ["wales", 52.3, -3.8], ["cardiff", 51.48, -3.18], ["swansea", 51.62, -3.94], ["north wales", 53.1, -3.8],
  ["pembrokeshire", 51.8, -4.9], ["powys", 52.3, -3.4], ["gwynedd", 52.9, -4.0],
  ["scotland", 56.8, -4.2], ["edinburgh", 55.95, -3.19], ["glasgow", 55.86, -4.25], ["aberdeen", 57.15, -2.1],
  ["dundee", 56.46, -2.97], ["highland", 57.48, -4.22], ["inverness", 57.48, -4.22], ["fife", 56.2, -3.15],
  ["lothian", 55.9, -3.1], ["ayrshire", 55.46, -4.63], ["dumfries", 55.07, -3.6], ["perth", 56.4, -3.43],
  ["northern ireland", 54.6, -6.7], ["belfast", 54.6, -5.93], ["derry", 54.99, -7.31], ["antrim", 54.86, -6.28],
  ["down", 54.33, -5.9], ["armagh", 54.35, -6.65], ["fermanagh", 54.35, -7.63], ["tyrone", 54.6, -7.3],
];

const NATIONS = { england: [52.6, -1.2], scotland: [56.8, -4.2], wales: [52.3, -3.8], "northern ireland": [54.6, -6.7] };

// UK postcode area (1–2 letters) → rough centroid.
const POSTCODE = {
  AB: [57.15, -2.1], AL: [51.75, -0.34], B: [52.48, -1.9], BA: [51.38, -2.36], BB: [53.75, -2.48],
  BD: [53.79, -1.75], BH: [50.73, -1.88], BL: [53.58, -2.43], BN: [50.83, -0.15], BR: [51.4, 0.02],
  BS: [51.45, -2.59], CA: [54.6, -2.9], CB: [52.2, 0.13], CF: [51.48, -3.18], CH: [53.19, -2.9],
  CM: [51.73, 0.47], CO: [51.89, 0.9], CR: [51.35, -0.1], CT: [51.28, 1.08], CV: [52.41, -1.51],
  CW: [53.1, -2.44], DA: [51.44, 0.22], DD: [56.46, -2.97], DE: [52.92, -1.48], DG: [55.07, -3.6],
  DH: [54.78, -1.58], DL: [54.53, -1.56], DN: [53.52, -0.93], DT: [50.71, -2.44], DY: [52.5, -2.13],
  E: [51.53, 0.02], EC: [51.52, -0.09], EH: [55.95, -3.19], EN: [51.65, -0.08], EX: [50.72, -3.53],
  FK: [56.0, -3.78], FY: [53.82, -3.02], G: [55.86, -4.25], GL: [51.86, -2.24], GU: [51.24, -0.58],
  HA: [51.58, -0.34], HD: [53.65, -1.78], HG: [54.0, -1.53], HP: [51.63, -0.75], HR: [52.06, -2.72],
  HS: [57.9, -6.8], HU: [53.74, -0.33], HX: [53.72, -1.86], IG: [51.56, 0.07], IP: [52.06, 1.15],
  IV: [57.48, -4.22], KA: [55.46, -4.63], KT: [51.35, -0.3], KW: [58.6, -3.5], KY: [56.13, -3.16],
  L: [53.41, -2.99], LA: [54.05, -2.8], LD: [52.24, -3.38], LE: [52.63, -1.13], LL: [53.15, -3.83],
  LN: [53.23, -0.54], LS: [53.8, -1.55], LU: [51.88, -0.42], M: [53.48, -2.24], ME: [51.35, 0.6],
  MK: [52.04, -0.76], ML: [55.79, -3.9], N: [51.56, -0.1], NE: [54.98, -1.6], NG: [52.95, -1.15],
  NN: [52.24, -0.9], NP: [51.58, -3.0], NR: [52.63, 1.29], NW: [51.55, -0.2], OL: [53.55, -2.11],
  OX: [51.75, -1.26], PA: [55.85, -4.42], PE: [52.57, -0.24], PH: [56.4, -3.43], PL: [50.38, -4.14],
  PO: [50.8, -1.09], PR: [53.76, -2.7], RG: [51.46, -0.97], RH: [51.1, -0.17], RM: [51.58, 0.18],
  S: [53.38, -1.47], SA: [51.62, -3.94], SE: [51.47, -0.05], SG: [51.9, -0.2], SK: [53.41, -2.13],
  SL: [51.51, -0.6], SM: [51.36, -0.19], SN: [51.56, -1.78], SO: [50.9, -1.4], SP: [51.07, -1.8],
  SR: [54.9, -1.38], SS: [51.54, 0.71], ST: [53.0, -2.18], SW: [51.46, -0.16], SY: [52.71, -2.75],
  TA: [51.02, -3.1], TD: [55.6, -2.6], TF: [52.68, -2.45], TN: [51.13, 0.27], TQ: [50.46, -3.53],
  TR: [50.26, -5.05], TS: [54.57, -1.23], TW: [51.44, -0.34], UB: [51.53, -0.4], W: [51.51, -0.2],
  WA: [53.39, -2.6], WC: [51.52, -0.12], WD: [51.66, -0.4], WF: [53.68, -1.5], WN: [53.55, -2.63],
  WR: [52.19, -2.22], WS: [52.59, -1.98], WV: [52.59, -2.13], YO: [53.96, -1.08], ZE: [60.15, -1.15],
};

export function resolvePoint(area, nationHint) {
  if (!area && !nationHint) return null;
  const a = String(area || "").trim();
  // 1) postcode prefix
  const m = a.toUpperCase().match(/\b([A-Z]{1,2})\d/);
  if (m && POSTCODE[m[1]]) return POSTCODE[m[1]];
  const low = a.toLowerCase();
  // 2) place keywords
  for (const [key, la, lo] of PLACES) if (low.includes(key)) return [la, lo];
  // 3) nation
  for (const key of Object.keys(NATIONS)) if (low.includes(key)) return NATIONS[key];
  if (nationHint && NATIONS[nationHint.toLowerCase()]) return NATIONS[nationHint.toLowerCase()];
  return null;
}

export const SEV_FILL = {
  severe: "#dc2626", warning: "#f59e0b", advisory: "#ca8a04", normal: "#16a34a",
};
